import { useEffect, useState } from "react";

import { useLocation } from "react-router-dom";

import { useAppState } from "@/components/AppStateProvider";
import LoggedOutNavigation from "@/components/navigation/common/LoggedOutNavigation";
import PractitionerLoggedInNavigation from "@/components/navigation/practitioner/PractitionerLoggedInNavigation";
import { HeaderProvider } from "@/contexts/HeaderContext";
import { useOrganisationBasketContext } from "@/contexts/organisations/OrganisationBasketContext";
import {
  CMP_DASHBOARD_URL,
  getCmpUrl,
  getWardUrl,
  SETTINGS_URL,
  WARDS_TESTS_URL
} from "@/core/urls";
import useOrganisation from "@/hooks/organisations/useOrganisation";
import { startsWith } from "@/utils/nodash";

import Container from "./Container";

const EXCEPTIONS = [];

function useHeader() {
  const location = useLocation();
  if (EXCEPTIONS.includes(location.pathname)) return false;
  // Hide header entirely on print views
  if (location.pathname.endsWith("/print")) return false;
  return true;
}

export default function CmpHeader({ hideNav = false, hideBasket = false }) {
  const {
    userInfo,
    practitionerBasketOpen: basketOpen,
    setPractitionerBasketOpen: setBasketOpen
  } = useAppState();
  const showHeader = useHeader();
  const { availableOrganisations } = useOrganisation();

  let header = <Container bg="blue" height={60} />;

  const organisationBasketContext = useOrganisationBasketContext();
  const basket = organisationBasketContext?.basket;
  const [basketCount, setBasketCount] = useState(0);

  useEffect(() => {
    if (basket) {
      let count = basket.productLineItems.reduce((total, item) => total + item.quantity, 0);
      let supplementCount = basket.supplementLineItems.reduce(
        (total, item) => total + item.quantity,
        0
      );
      setBasketCount(count + supplementCount);
    }
  }, [basket]);

  if (!showHeader) {
    return null;
  }

  if (userInfo) {
    header = userInfo.user ? (
      <PractitionerLoggedInNavigation
        accounts={[
          {
            label: "Personal Health Data",
            links: [
              {
                label: `${userInfo.user.firstName} ${userInfo.user.lastName}`,
                to: SETTINGS_URL
              },
              ...(userInfo.user.patientProfile?.wards ?? []).map(ward => ({
                label: `${ward.user.firstName} ${ward.user.lastName}`,
                to: getWardUrl(WARDS_TESTS_URL, { patientId: ward.user.id })
              }))
            ]
          },
          {
            label: "Organisations",
            links:
              availableOrganisations?.map(organisation => ({
                label: organisation.name,
                to: getCmpUrl(CMP_DASHBOARD_URL, { organisationId: organisation.hashId }),
                activeTest: () => {
                  return startsWith(location.pathname, `/cmp/organisations/${organisation.hashId}`);
                }
              })) || []
          }
        ]}
        hideNav={hideNav}
        hideBasket={hideBasket}
        basketCount={basketCount}
        onBasketClick={() => setBasketOpen(!basketOpen)}
      />
    ) : (
      <LoggedOutNavigation />
    );
  }

  return <HeaderProvider>{header}</HeaderProvider>;
}
